import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { DeviceEnrollmentOptions, NewUserFormInput } from '@xemplo/gp-types';
import { RadioGroupV2 } from '@xemplo/radio-button';
import { ToggleButton } from '@xemplo/switch';

import {
  EnrollmentOptsMfaDisabled,
  EnrollmentOptsMfaEnabled,
} from './user-form-sections.const';
import * as S from './user-form-sections.styles';
import { PreferenceSectionProps } from './user-form-sections.types';

export function PreferenceSection({
  editingUser,
  showSystemAdminFields = false,
}: PreferenceSectionProps) {
  const { setValue } = useFormContext<NewUserFormInput>();

  const mfaEnable = useWatch({
    name: 'mfaEnable',
    defaultValue: editingUser?.mfaEnable,
  });

  const deviceEnrolmentEnable = useWatch({ name: 'deviceEnrolmentEnable' });

  const options = useMemo(
    () => (mfaEnable === 'true' ? EnrollmentOptsMfaEnabled : EnrollmentOptsMfaDisabled),
    [mfaEnable],
  );

  useEffect(() => {
    const isValid = options.some(
      (o) => o.value.toString() === deviceEnrolmentEnable?.toString(),
    );
    if (!isValid)
      setValue('deviceEnrolmentEnable', DeviceEnrollmentOptions.Disabled, {
        shouldValidate: false,
      });
  }, [options, deviceEnrolmentEnable, setValue]);

  return (
    <>
      <S.Fieldset>
        <S.Legend>Preferences</S.Legend>
        <S.Label>
          Receive notifications
          <ToggleButton
            name="getNotifications"
            items={[
              { label: 'Yes', value: 'true', selected: editingUser?.getNotifications },
              {
                label: 'No',
                value: 'false',
                selected: editingUser && !editingUser.getNotifications,
              },
            ]}
          />
        </S.Label>
        {showSystemAdminFields && (
          <S.Label>
            Enable Multi-Factor Authentication
            <ToggleButton
              name="mfaEnable"
              items={[
                { label: 'Yes', value: 'true', selected: editingUser?.mfaEnable },
                {
                  label: 'No',
                  value: 'false',
                  selected: editingUser && !editingUser.mfaEnable,
                },
              ]}
            />
          </S.Label>
        )}
      </S.Fieldset>
      {showSystemAdminFields && (
        <S.Fieldset>
          <S.RadioGroupWrapper>
            <S.RadioGroupTitle>Device enrollment</S.RadioGroupTitle>
            <RadioGroupV2
              ariaLabel="Device enrollment option"
              name="deviceEnrolmentEnable"
              options={options}
              rules={{ required: 'Please select an option' }}
              showLabel
            />
          </S.RadioGroupWrapper>
        </S.Fieldset>
      )}
    </>
  );
}
